import { useQueryClient } from '@tanstack/react-query';
import { useMemoManager } from './useMemoManager';
import { useMemoIndexManager } from './useMemoIndexManager';
import { useMemoObjectManager } from './useMemoObjectManager';
import { useMemoTagManager } from './useMemoTagManager';
import { memoKeys } from '../api/memoAPI';
import { DataType, AnalysisUpdateMessage } from '../../common/MetaData';
import { handleError, useErrorHandler } from './errorHandler';

/**
 * 分析更新消息集成Hook
 * 负责处理WebSocket接收到的ANALYSIS_UPDATE消息
 * - 通过Zustand store更新UI状态
 * - 通过React Query管理缓存失效
 */
export const useAnalysisUpdateManager = () => {
  const queryClient = useQueryClient();
  const { forceRefresh } = useMemoManager();
  const { forceRefresh: forceRefreshMemoIndex } = useMemoIndexManager();
  const { forceRefresh: forceRefreshMemoObject } = useMemoObjectManager('');
  const { forceRefresh: forceRefreshMemoTag } = useMemoTagManager();

  // WebSocket消息处理应该只针对单个memo进行精细化刷新，避免不必要的全量刷新
  // 移除所有列表刷新逻辑，只处理单个memo相关的数据

  /**
   * 强制刷新本地memo数据
   * 1. 让本地的缓存失效，确保React Query缓存一致性
   * 2. 调用forceRefresh方法，确保数据更新到Zustand store并触发UI更新
   */
  const refreshMemoData = async (memoId: string) => {
    try {      
      // 1. 让本地的缓存失效，确保React Query缓存一致性
      const memoCacheKey = memoKeys.memo(memoId);
      await queryClient.invalidateQueries({ queryKey: memoCacheKey });
      
      // 2. 调用forceRefresh方法，传入WebSocket消息中的memoId，只刷新指定的memo
      await forceRefresh(memoId);
    } catch (error) {
      handleError(error, { context: 'refreshMemoData', showToast: true });
      throw error; // 重新抛出错误，让上层处理
    }
  };

  /**
   * 处理分析更新事件
   * 根据不同的DataType，采取相应的缓存失效和刷新策略
   */
  const handleAnalysisUpdate = async (data: AnalysisUpdateMessage) => {
    // 直接调用refreshMemoData，不需要重复判断
    await refreshMemoData(data.targetId);
    
    switch (data.dataType) {
      case DataType.MEMO:
        break;
        
      case DataType.INDEX:
        // 强制刷新特定memo的index数据，无论本地是否存在缓存
        if (data.targetId) {
          // 使用Manager层方法强制刷新memo index数据
          await forceRefreshMemoIndex();
        }
        break;
        
      case DataType.TAG:
        // 强制刷新特定memo的标签数据，无论本地是否存在缓存
        if (data.targetId) {
          // 使用Manager层方法强制刷新memo标签数据
          await forceRefreshMemoTag(data.targetId);
        }
        break;
        
      case DataType.OBJECT:
        // 强制刷新特定memo的对象数据，无论本地是否存在缓存
        if (data.targetId) {
          // 使用Manager层方法强制刷新memo object数据
          await forceRefreshMemoObject();
        }
        break;
        
      default:
        break;
    }
  };

  return {
    handleAnalysisUpdate
  };
};